/**
 * Latin squares for counterbalancing condition order in a within-subjects design.
 *
 * Conditions are numbered 0…n − 1; a design is a list of orders (rows), each a
 * permutation of the conditions.
 *
 * - **Cyclic square**: row i is i, i + 1, …, wrapping round. Every condition appears once
 *   in every position, but each is always followed by the same condition, so first-order
 *   carryover is confounded with the conditions themselves.
 * - **Balanced square (Williams, 1949)**: the first row is 0, 1, n − 1, 2, n − 2, 3, …
 *   (Bradley, 1958) and each later row adds 1 (mod n). For even n that one square has every
 *   condition immediately followed by every other exactly once. For odd n the construction
 *   cannot do that in one square, so the design is the square plus its mirror image (every
 *   row reversed): 2n orders in which each condition precedes every other exactly twice.
 */

import {
  type RandomSource,
  shuffle,
} from "@/lib/tool-kit/research-design/random";

export type LatinDesign = "balanced" | "cyclic";

export const MAX_CONDITIONS = 26;

/** Williams' first row: 0, 1, n − 1, 2, n − 2, 3, … */
export function williamsFirstRow(n: number): number[] {
  const row = [0];
  let low = 1;
  let high = n - 1;
  for (let position = 1; position < n; position++) {
    if (position % 2 === 1) {
      row.push(low);
      low += 1;
    } else {
      row.push(high);
      high -= 1;
    }
  }
  return row;
}

function shifted(first: readonly number[], n: number): number[][] {
  return Array.from({ length: n }, (_, i) =>
    first.map((condition) => (condition + i) % n)
  );
}

export function cyclicSquare(n: number): number[][] {
  return shifted(
    Array.from({ length: n }, (_, i) => i),
    n
  );
}

/** n orders for even n; 2n (the square, then its mirror) for odd n. */
export function williamsDesign(n: number): number[][] {
  const square = shifted(williamsFirstRow(n), n);
  return n % 2 === 0
    ? square
    : [...square, ...square.map((row) => [...row].reverse())];
}

export function buildDesign(n: number, design: LatinDesign): number[][] {
  return design === "balanced" ? williamsDesign(n) : cyclicSquare(n);
}

/** counts[a][b]: how often condition a is immediately followed by condition b. */
export function carryoverCounts(
  rows: readonly (readonly number[])[],
  n: number
): number[][] {
  const counts = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  for (const row of rows) {
    for (let position = 0; position + 1 < row.length; position++) {
      counts[row[position]][row[position + 1]] += 1;
    }
  }
  return counts;
}

/** Every condition appears equally often in every position. */
export function positionBalanced(
  rows: readonly (readonly number[])[],
  n: number
): boolean {
  for (let position = 0; position < n; position++) {
    const seen = new Array<number>(n).fill(0);
    for (const row of rows) {
      seen[row[position]] += 1;
    }
    if (seen.some((count) => count !== seen[0])) {
      return false;
    }
  }
  return true;
}

/**
 * The number of times each ordered pair occurs when every pair occurs equally often, or
 * null when some pair occurs more often than another.
 */
export function carryoverBalance(
  rows: readonly (readonly number[])[],
  n: number
): number | null {
  const counts = carryoverCounts(rows, n);
  const pairs = counts.flatMap((row, a) => row.filter((_, b) => a !== b));
  return pairs.every((count) => count === pairs[0]) ? (pairs[0] ?? null) : null;
}

/**
 * The order (row index) each participant follows. Rows are used once per cycle, so the
 * design stays balanced whenever the participant count is a multiple of the row count.
 * With a source, each cycle's row order is shuffled; without, rows run 1, 2, 3, ….
 */
export function rowsForParticipants(
  participants: number,
  rowCount: number,
  next?: RandomSource
): number[] {
  const rows: number[] = [];
  const base = Array.from({ length: rowCount }, (_, i) => i);
  while (rows.length < participants) {
    const cycle = next ? shuffle(base, next) : base;
    rows.push(...cycle.slice(0, participants - rows.length));
  }
  return rows;
}
